import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import { MailEntity } from '@source/mails/entities/mail.js';
import { AnsweringEntity } from '../entities/answering.js';
import { BadcontactEntity } from '../entities/badcontact.js';
import { ContactEntity } from '../entities/contact.js';
import { IntercomEntity } from '../entities/intercom.js';
import { OdooEntity } from '../entities/odoo.js';

// Mutation to add a comment to the comments array
export const addConversationComment = registerMutation({
  operationName: 'addConversationComment',
  type: () => Boolean,
  params: [
    { name: 'conversationID', type: () => String },
    { name: 'comment', type: () => String },
    { name: 'entityType', type: () => String },
  ],
  resolve: (conversationID: string, comment: string, entityType: string | null | undefined) => async () => {
    // Fetch the current entity
    let conversation = await getByID(
      MailEntity,
      conversationID,
    )({
      _id: 1,
      comments: 1,
    });
    if (entityType && entityType === 'answering') {
      conversation = await getByID(
        AnsweringEntity,
        conversationID,
      )({
        _id: 1,
        comments: 1,
      });
    }

    if (!conversation) {
      throw new Error(`Conversation with ID ${conversationID} not found`);
    }

    // Add the new comment to the array
    conversation.comments ??= [];
    conversation.comments.push(comment);

    // Update the entity with the new comments array
    await conversation.save();

    // Return success
    return true;
  },
});

export const setOdooConversationControlled = registerMutation({
  operationName: 'setOdooConversationControlled',
  type: () => Boolean,
  params: [{ name: 'conversationID', type: () => String }],
  resolve: (conversationID: string) => async () => {
    // Fetch the current entity
    const conversation = await getByID(
      OdooEntity,
      conversationID,
    )({
      _id: 1,
      isCheckedBySupport: 1,
    });
    if (!conversation) {
      throw new Error(`Intercom with ID ${conversationID} not found`);
    }
    conversation.isCheckedBySupport = !conversation.isCheckedBySupport;
    await conversation.save();
    return true;
  },
});

export const toggleCheckedAsIrrelevant = registerMutation({
  operationName: 'toggleCheckedAsIrrelevant',
  type: () => Boolean,
  params: [{ name: 'conversationID', type: () => String }],
  resolve: (conversationID: string) => async () => {
    const conversation = await getByID(
      MailEntity,
      conversationID,
    )({
      _id: 1,
      checkedAsIrrelevant: 1,
      from: 1,
    });

    if (!conversation) {
      throw new Error(`Conversation with ID ${conversationID} not found`);
    }

    conversation.checkedAsIrrelevant = !conversation.checkedAsIrrelevant;
    const status = conversation.checkedAsIrrelevant;
    const fromSender = conversation.from;

    await conversation.save();

    // Update all other conversations with the same "from" sender
    const collection = await getCollection<MailEntity>(MailEntity._class);

    const conversations = await collection.find({ from: fromSender }).toArray();

    for (const conv of conversations) {
      conv.checkedAsIrrelevant = status;
      await collection.updateOne(
        { _id: conv._id },
        { $set: { checkedAsIrrelevant: status, suggestedAsIrrelevant: null } },
      );
    }

    return true;
  },
});

export const setAnsweringCheckedByHuman = registerMutation({
  operationName: 'setAnsweringCheckedByHuman',
  type: () => Boolean,
  params: [{ name: 'answeringID', type: () => String }],
  resolve: (answeringID: string) => async () => {
    // Fetch the current entity
    const answering = await getByID(
      AnsweringEntity,
      answeringID,
    )({
      _id: 1,
      checkedByHuman: 1,
    });
    if (!answering) {
      throw new Error(`Answering with ID ${answeringID} not found`);
    }
    answering.checkedByHuman = true;
    await answering.save();
    return true;
  },
});

export const setIntercomConversationControlled = registerMutation({
  operationName: 'setIntercomConversationControlled',
  type: () => Boolean,
  params: [{ name: 'intercomID', type: () => String }],
  resolve: (intercomID: string) => async () => {
    // Fetch the current entity
    const intercom = await getByID(
      IntercomEntity,
      intercomID,
    )({
      _id: 1,
      isCheckedBySupport: 1,
    });
    if (!intercom) {
      throw new Error(`Intercom with ID ${intercomID} not found`);
    }
    intercom.isCheckedBySupport = !intercom.isCheckedBySupport;
    await intercom.save();
    return true;
  },
});

export const setBadContactControlled = registerMutation({
  operationName: 'setBadContactControlled',
  type: () => Boolean,
  params: [{ name: 'contactID', type: () => String }],
  resolve: (contactID: string) => async () => {
    // Fetch the current entity
    const contact = await getByID(
      BadcontactEntity,
      contactID,
    )({
      _id: 1,
      isCheckedBySupport: 1,
    });
    if (!contact) {
      throw new Error(`Intercom with ID ${contactID} not found`);
    }
    contact.isCheckedBySupport = !contact.isCheckedBySupport;
    await contact.save();
    return true;
  },
});

export const setContactControlled = registerMutation({
  operationName: 'setContactControlled',
  type: () => Boolean,
  params: [{ name: 'contactID', type: () => String }],
  resolve: (contactID: string) => async () => {
    // Fetch the current entity
    const contact = await getByID(
      ContactEntity,
      contactID,
    )({
      _id: 1,
      isCheckedBySupport: 1,
    });
    if (!contact) {
      throw new Error(`Intercom with ID ${contactID} not found`);
    }
    contact.isCheckedBySupport = !contact.isCheckedBySupport;
    await contact.save();
    return true;
  },
});

// Simplified version that only toggles the entity field without calling Odoo services
export const toggleIsOdooActive = registerMutation({
  operationName: 'toggleIsOdooActive',
  type: () => Boolean,
  params: [{ name: 'conversationID', type: () => String }],
  resolve: (conversationID: string) => async () => {
    const ticket = await getByID(
      OdooEntity,
      conversationID,
    )({
      _id: 1,
      isOdooActive: 1,
    });

    if (!ticket) {
      throw new Error(`Conversation with ID ${conversationID} not found`);
    }

    ticket.isOdooActive = ticket.isOdooActive === false ? true : false;
    await ticket.save();
    // Note: This simplified version does not call setHelpdeskTicketArchived
    // The full functionality requires server-side processing
    return ticket.isOdooActive;
  },
});

// Simplified version that creates basic OdooEntity without complex Odoo integrations
export const loadEmailToOdooManually = registerMutation({
  operationName: 'loadEmailToOdooManually',
  type: () => Boolean,
  params: [{ name: 'conversationID', type: () => String }],
  resolve: (conversationID: string) => async () => {
    // Fetch the current entity
    const conversation = await getByID(
      MailEntity,
      conversationID,
    )({
      _id: 1,
      subject: 1,
      body: 1,
      from: 1,
      odooTicketID: 1,
    });

    if (!conversation) {
      throw new Error(`Conversation with ID ${conversationID} not found`);
    }

    const subject: string = conversation.subject || '';
    const body: string = conversation.body || '';
    const from: string = conversation.from || '';

    // Create new OdooEntity without complex Odoo service integrations
    const odooEntity = new OdooEntity({
      subject: subject,
      body: body,
      from: from,
      attachments: [],
      messages: [],
      odooTags: [],
      comments: [],
      isCheckedBySupport: false,
      isOdooActive: true,
    });
    await odooEntity.create();

    // Note: This simplified version does not integrate with actual Odoo services
    // The full functionality requires server-side processing with environment access
    return true;
  },
});
